/**
 * Header Component - Navigation and status display
 * Shows app title, connection status, and last update time
 */

export interface HeaderStatus {
  isOnline: boolean;
  isLoading: boolean;
  lastUpdated: Date | null;
  error: string | null;
}

export class Header {
  private container: HTMLElement | null = null;

  constructor() {}

  render(container: HTMLElement): void {
    this.container = container;
    
    container.innerHTML = `
      <header class="header" role="banner">
        <div class="header-content">
          <div class="header-brand">
            <h1 class="header-title">
              <div class="header-icon">🏥</div>
              Sierra Leone Mpox Tracker
            </h1>
            <div class="header-subtitle">
              Emergency Response Dashboard • Real-time Health Data
            </div>
          </div>
          
          <div class="header-status">
            <div class="system-status">
              <div class="status-indicator" id="connection-status"></div>
              <span class="status-text" id="status-text">Connecting...</span>
            </div>
            <div class="last-updated" id="last-updated">
              Loading...
            </div>
          </div>
        </div>
      </header>
    `;

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    if (!this.container) return;

    // Refresh button
    const refreshBtn = this.container.querySelector('#refresh-btn');
    refreshBtn?.addEventListener('click', () => {
      const event = new CustomEvent('data:refresh');
      document.dispatchEvent(event);
      
      // Visual feedback
      this.showRefreshAnimation();
    });

    // Info button
    const infoBtn = this.container.querySelector('#info-btn');
    infoBtn?.addEventListener('click', () => {
      this.showInfoModal();
    });
  }

  private showRefreshAnimation(): void {
    const refreshIcon = this.container?.querySelector('.refresh-icon');
    if (refreshIcon) {
      refreshIcon.classList.add('rotating');
      setTimeout(() => {
        refreshIcon.classList.remove('rotating');
      }, 1000);
    }
  }

  private showInfoModal(): void {
    // Create modal overlay
    const modal = document.createElement('div');
    modal.className = 'modal-overlay';
    modal.innerHTML = `
      <div class="modal-content" role="dialog" aria-labelledby="modal-title" aria-modal="true">
        <div class="modal-header">
          <h2 id="modal-title">About Sierra Leone Mpox Tracker</h2>
          <button class="modal-close" aria-label="Close modal">×</button>
        </div>
        <div class="modal-body">
          <p>
            <strong>🇸🇱 Sierra Leone Mpox Tracker</strong> provides real-time information 
            about the mpox outbreak across all 16 districts in Sierra Leone.
          </p>
          
          <h3>Features:</h3>
          <ul>
            <li>📊 Real-time district statistics</li>
            <li>🗺️ Interactive outbreak map</li>
            <li>💉 Vaccine site locations</li>
            <li>📱 Mobile-optimized design</li>
            <li>🔄 Offline functionality</li>
          </ul>
          
          <h3>Data Sources:</h3>
          <p>
            Data is collected from health facilities across Sierra Leone and 
            updated every 30 minutes. For emergencies, call <strong>117</strong>.
          </p>
          
          <h3>Accessibility:</h3>
          <p>
            This tracker is designed to work on all devices, including basic 
            smartphones with 2G connections. It follows WCAG 2.1 AA guidelines.
          </p>
          
          <div class="contact-info">
            <p>
              <strong>Emergency:</strong> Call 117<br>
              <strong>Ministry of Health:</strong> +232 22 224 236<br>
              <strong>Technical Support:</strong> support@health.gov.sl
            </p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-primary modal-close">Close</button>
        </div>
      </div>
    `;

    // Add modal to page
    document.body.appendChild(modal);

    // Setup close handlers
    const closeButtons = modal.querySelectorAll('.modal-close');
    const closeModal = () => {
      modal.remove();
      document.body.style.overflow = '';
    };

    closeButtons.forEach(btn => {
      btn.addEventListener('click', closeModal);
    });

    // Close on overlay click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    // Close on escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);

    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    // Focus management
    const firstButton = modal.querySelector('button');
    firstButton?.focus();
  }

  updateConnectionStatus(isOnline: boolean): void {
    if (!this.container) return;

    const statusIndicator = this.container.querySelector('#connection-status');
    const statusText = this.container.querySelector('#status-text');

    if (statusIndicator && statusText) {
      if (isOnline) {
        statusIndicator.className = 'status-indicator';
        statusText.textContent = 'System Operational';
      } else {
        statusIndicator.className = 'status-indicator error';
        statusText.textContent = 'Connection Issues';
      }
    }
  }

  updateStatus(status: HeaderStatus): void {
    if (!this.container) return;

    this.updateConnectionStatus(status.isOnline);

    // Update last updated time
    const lastUpdatedElement = this.container.querySelector('#last-updated');
    if (lastUpdatedElement && status.lastUpdated) {
      lastUpdatedElement.textContent = `Last updated: ${this.formatLastUpdated(status.lastUpdated)}`;
    }

    // Update status text based on error state
    const statusText = this.container.querySelector('#status-text');
    const statusIndicator = this.container.querySelector('#connection-status');
    
    if (statusText && statusIndicator) {
      if (status.error) {
        statusText.textContent = 'Data Error';
        statusIndicator.className = 'status-indicator error';
      } else if (status.isLoading) {
        statusText.textContent = 'Updating Data...';
        statusIndicator.className = 'status-indicator warning';
      } else {
        statusText.textContent = status.isOnline ? 'System Operational' : 'Connection Issues';
        statusIndicator.className = status.isOnline ? 'status-indicator' : 'status-indicator error';
      }
    }
  }

  private formatLastUpdated(date: Date): string {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    // Less than 1 minute
    if (diff < 60000) {
      return 'Just now';
    }
    
    // Less than 1 hour
    if (diff < 3600000) {
      const minutes = Math.floor(diff / 60000);
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    }
    
    // Less than 1 day
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    }
    
    // More than 1 day - show actual time
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private addStyles(): void {
    // Add component-specific styles
    const style = document.createElement('style');
    style.textContent = `
      .header-content {
        display: grid;
        grid-template-columns: 1fr auto auto;
        gap: var(--spacing-4);
        align-items: center;
      }
      
      @media (max-width: 768px) {
        .header-content {
          grid-template-columns: 1fr;
          gap: var(--spacing-3);
          text-align: center;
        }
        
        .header-status {
          order: 2;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: var(--font-size-sm);
        }
        
        .header-actions {
          order: 3;
          display: flex;
          justify-content: center;
          gap: var(--spacing-2);
        }
      }
      
      .last-updated {
        font-size: var(--font-size-xs);
        opacity: 0.9;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
      }
      
      @media (max-width: 768px) {
        .last-updated {
          align-items: center;
        }
      }
      
      .header-actions {
        display: flex;
        gap: var(--spacing-2);
      }
      
      .refresh-icon.rotating {
        animation: spin 1s linear infinite;
      }
      
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      
      .btn.loading {
        opacity: 0.6;
        cursor: not-allowed;
      }
      
      /* Modal styles */
      .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        padding: var(--spacing-4);
      }
      
      .modal-content {
        background: white;
        border-radius: var(--radius-lg);
        box-shadow: var(--shadow-lg);
        max-width: 500px;
        width: 100%;
        max-height: 90vh;
        overflow: auto;
      }
      
      .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var(--spacing-6);
        border-bottom: 1px solid var(--gray-200);
      }
      
      .modal-header h2 {
        margin: 0;
        color: var(--primary-color);
      }
      
      .modal-close {
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
        padding: var(--spacing-1);
        color: var(--gray-500);
      }
      
      .modal-close:hover {
        color: var(--gray-700);
      }
      
      .modal-body {
        padding: var(--spacing-6);
      }
      
      .modal-body h3 {
        color: var(--primary-color);
        margin: var(--spacing-4) 0 var(--spacing-2);
      }
      
      .modal-body ul {
        margin-left: var(--spacing-4);
        margin-bottom: var(--spacing-4);
      }
      
      .contact-info {
        background: var(--gray-50);
        padding: var(--spacing-4);
        border-radius: var(--radius);
        margin-top: var(--spacing-4);
      }
      
      .modal-footer {
        padding: var(--spacing-6);
        border-top: 1px solid var(--gray-200);
        text-align: right;
      }
    `;
    
    document.head.appendChild(style);
  }
}